import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Typography,
    Box,
    IconButton,
    Alert,
} from '@mui/material';
import { Close, Visibility, VisibilityOff } from '@mui/icons-material';
import api from '../../api/client';

const ChangePasswordDialog = ({ open, onClose, onSuccess, onError }) => {
    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [showPasswords, setShowPasswords] = useState({
        old: false,
        new: false,
        confirm: false,
    });

    // Password validation function
    const validatePassword = () => {
        const newErrors = {};
        
        // Old password validation
        if (!passwordData.oldPassword.trim()) {
            newErrors.oldPassword = 'Old password is required';
        }

        // New password validation
        if (!passwordData.newPassword.trim()) {
            newErrors.newPassword = 'New password is required';
        } else {
            const password = passwordData.newPassword;
            const validationErrors = [];

            // Minimum 12 characters
            if (password.length < 12) {
                validationErrors.push('at least 12 characters');
            }

            // At least one uppercase letter
            if (!/[A-Z]/.test(password)) {
                validationErrors.push('one uppercase letter');
            }

            // At least one special character
            if (!/[!@#$%^&*(),.?":|<>]/.test(password)) {
                validationErrors.push('one special character (!@#$%^&*(),.?":|<>)');
            }

            // At least one number
            if (!/[0-9]/.test(password)) {
                validationErrors.push('one number');
            }

            if (validationErrors.length > 0) {
                newErrors.newPassword = `Password must contain ${validationErrors.join(', ')}`;
            }
        }

        // Confirm password validation
        if (!passwordData.confirmPassword.trim()) {
            newErrors.confirmPassword = 'Confirm password is required';
        } else if (passwordData.newPassword !== passwordData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value,
        }));
        
        // Clear error when user types
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: '',
            }));
        }
    };

    const togglePasswordVisibility = (field) => {
        setShowPasswords(prev => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handleSubmit = async () => {
        if (!validatePassword()) {
            return;
        }

        setLoading(true);
        try {
            const payload = {
                old_password: passwordData.oldPassword,
                new_password: passwordData.newPassword,
                confirm_password: passwordData.confirmPassword,
            };

            const res = await api.post('/auth/change-password/', payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `${localStorage.getItem('adminToken')}`,
                },
            });

            if (res) {
                onSuccess('Password changed successfully');
                handleClose();
            }
        } catch (err) {
            console.error('Password change error:', err);
            const errorMessage = err.response?.data?.message || 
                               err.response?.data?.error || 
                               err.response?.data?.detail ||
                               err.message || 
                               'Failed to change password';
            onError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setPasswordData({
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
        });
        setErrors({});
        setShowPasswords({
            old: false,
            new: false,
            confirm: false,
        });
        onClose();
    };

    const getPasswordStrengthColor = () => {
        const password = passwordData.newPassword;
        if (!password) return 'grey';
        
        let score = 0;
        if (password.length >= 12) score++;
        if (/[A-Z]/.test(password)) score++;
        if (/[!@#$%^&*(),.?":|<>]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;

        if (score === 4) return 'success';
        if (score >= 2) return 'warning';
        return 'error';
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                    Change Password
                </Typography>
                <IconButton 
                    onClick={handleClose}
                    size="small"
                    sx={{ color: 'grey.500' }}
                    disabled={loading}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            
            <DialogContent sx={{ pt: 2 }}>
                <Box display="flex" flexDirection="column" gap={2.5}>
                    {/* Password Requirements Alert */}
                    <Alert severity="info" sx={{ mb: 1 }}>
                        <Typography variant="body2" fontWeight={600} gutterBottom>
                            Password Requirements:
                        </Typography>
                        <Typography variant="caption" component="div">
                            • Minimum 12 characters<br/>
                            • At least one uppercase letter<br/>
                            • At least one special character (!@#$%^&*(),.?":|&lt;&gt;)<br/>
                            • At least one number
                        </Typography>
                    </Alert>

                    {/* Old Password */}
                    <TextField
                        fullWidth
                        type={showPasswords.old ? 'text' : 'password'}
                        label="Current Password"
                        name="oldPassword"
                        value={passwordData.oldPassword}
                        onChange={handleInputChange}
                        error={!!errors.oldPassword}
                        helperText={errors.oldPassword}
                        variant="outlined"
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => togglePasswordVisibility('old')}
                                    edge="end"
                                    size="small"
                                >
                                    {showPasswords.old ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            ),
                        }}
                    />

                    {/* New Password */}
                    <TextField
                        fullWidth
                        type={showPasswords.new ? 'text' : 'password'}
                        label="New Password"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handleInputChange}
                        error={!!errors.newPassword}
                        helperText={errors.newPassword}
                        variant="outlined"
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => togglePasswordVisibility('new')}
                                    edge="end"
                                    size="small"
                                >
                                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            ),
                        }}
                    />

                    {/* Password Strength Indicator */}
                    {passwordData.newPassword && (
                        <Box>
                            <Typography variant="caption" color={getPasswordStrengthColor()}>
                                Password Strength: {
                                    getPasswordStrengthColor() === 'success' ? 'Strong' :
                                    getPasswordStrengthColor() === 'warning' ? 'Medium' : 'Weak'
                                }
                            </Typography>
                            <Box 
                                sx={{ 
                                    width: '100%', 
                                    height: 4, 
                                    backgroundColor: 'grey.300', 
                                    borderRadius: 2,
                                    mt: 0.5 
                                }}
                            >
                                <Box
                                    sx={{
                                        width: `${(passwordData.newPassword.length >= 12 ? 1 : 0) * 25 + 
                                                (/[A-Z]/.test(passwordData.newPassword) ? 1 : 0) * 25 + 
                                                (/[!@#$%^&*(),.?":|<>]/.test(passwordData.newPassword) ? 1 : 0) * 25 + 
                                                (/[0-9]/.test(passwordData.newPassword) ? 1 : 0) * 25}%`,
                                        height: '100%',
                                        backgroundColor: getPasswordStrengthColor() === 'success' ? 'success.main' :
                                                        getPasswordStrengthColor() === 'warning' ? 'warning.main' : 'error.main',
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                    }}
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Confirm Password */}
                    <TextField
                        fullWidth
                        type={showPasswords.confirm ? 'text' : 'password'}
                        label="Confirm New Password"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handleInputChange}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        variant="outlined"
                        disabled={loading}
                        InputProps={{
                            endAdornment: (
                                <IconButton
                                    onClick={() => togglePasswordVisibility('confirm')}
                                    edge="end"
                                    size="small"
                                >
                                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            ),
                        }}
                    />
                </Box>
            </DialogContent>
            
            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button 
                    onClick={handleClose} 
                    variant="outlined" 
                    color="secondary"
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary"
                    disabled={loading}
                >
                    {loading ? 'Changing...' : 'Change Password'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ChangePasswordDialog;